/* eslint-disable */
// @ts-nocheck

import {
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Grid,
  Popover,
  Separator,
  Tabs,
  Text,
  TextArea,
  Tooltip,
} from "@radix-ui/themes";
import { useEffect, useState, useRef } from "react";
import {
  useAnalysisResults,
  useChatWithAgent,
} from "../../../utils/queries/brandcheck";
import { useUI } from "../../../contexts/ui";
import { BrandCheckUtils } from "../../../services/brandcheck";
import cx from "classnames";
import {
  X,
  XCircle,
  Warning,
  CheckCircle,
  ArrowUp,
  Check,
  Prohibit,
  PlusIcon,
  ShareNetworkIcon,
} from "@phosphor-icons/react";
import { ConnectedAppsDialog } from "./ConnectedAppsDialog";
import { ChatMessage, RobotIcon } from "./ChatMessage";
import { ConnectMoreData } from "./ConnectMoreData";
import { RiskCardSkeleton } from "./RiskCardSkeleton";
import { QuickSuggestions } from "./QuickSuggestions";
import { RiskContextIndicator } from "./RiskContextIndicator";
import { resultsRoute } from "../results";
import { useSearch } from "@tanstack/react-router";
import { ArrowsOutSimpleIcon } from "@phosphor-icons/react/dist/ssr";

interface RiskItem {
  id: string;
  title: string;
  description: string;
  severity: "high" | "medium" | "low";
  percentage: number;
  isExpanded: boolean;
  sourceUrl?: string;
}

interface ChatMessage {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: number;
  sourceUrl?: string;
}

const Pin = ({
  priority,
  x,
  y,
}: {
  priority: string;
  x?: number;
  y?: number;
}) => {
  if (!x && !y) return null;
  const sentiments = {
    low: {
      color: "#3D9A50",
      borderColor: "#B7DFBA",
      icon: <Check size={16} color="white" />,
      title: "Low Risk",
    },
    medium: {
      color: "#FFB627",
      borderColor: "#FFCE78",
      icon: <Warning size={16} />,
      title: "Medium Risk",
    },
    high: {
      color: "#DE2400",
      borderColor: "#FFB9AA",
      icon: <Prohibit size={16} color="white" />,
      title: "High Risk",
    },
  };
  if (!sentiments[priority as keyof typeof sentiments]) return null;

  return (
    <Flex
      className="absolute size-6 rounded-full"
      justify="center"
      align="center"
      style={{
        backgroundColor: sentiments[priority as keyof typeof sentiments].color,
        borderWidth: 1,
        borderColor:
          sentiments[priority as keyof typeof sentiments].borderColor,
        left: `${x}%`,
        top: `${y}%`,
      }}
    >
      {sentiments[priority as keyof typeof sentiments].icon}
    </Flex>
  );
};

// Generic polite thinking messages for any user question
export const THINKING_MESSAGES = [
  "Thinking...",
  "Just a moment...",
  "Let me think about that...",
  "Processing your question...",
  "One moment please...",
  "Gathering my thoughts...",
  "Working on your answer...",
  "Let me consider this...",
  "Give me just a second...",
  "Analyzing your question...",
  "Formulating a response...",
  "Looking into this for you...",
  "Let me review this...",
  "Processing the information...",
  "Putting together an answer...",
  "Considering your question...",
  "Working through this...",
  "Almost ready with an answer...",
  "Let me check on that...",
  "Reviewing the details...",
  "Organizing my thoughts...",
  "Preparing a response...",
  "Give me a moment to think...",
  "Working on this for you...",
  "Let me process that...",
];

// Function to get a random thinking message
const getRandomThinkingMessage = () => {
  return THINKING_MESSAGES[
    Math.floor(Math.random() * THINKING_MESSAGES.length)
  ];
};

export function ResultsView() {
  const { sessionId } = useSearch({ from: resultsRoute.id });
  const { data: analysisResults, isLoading: isLoadingResults } =
    useAnalysisResults(sessionId || null);
  const { mutateAsync: chatWithAgent } = useChatWithAgent(sessionId || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { notify } = useUI();

  const [risks, setRisks] = useState<RiskItem[]>([
    {
      id: "1",
      title: "Claim: &ldquo;Clinically proven&rdquo;",
      description:
        "No citation found. The claim &apos;Our revolutionary formula is clinically proven to deliver...&apos; requires a citation or clinical study reference.",
      severity: "high",
      percentage: 84,
      isExpanded: false,
      sourceUrl: "acme.com/products/revolutionary-formula",
    },
    {
      id: "2",
      title: "▲ Brand color: your header blue",
      description: "Color is #1F7AB3, spec says #1E7BB3",
      severity: "medium",
      percentage: 91,
      isExpanded: false,
      sourceUrl: "acme.com/header",
    },
    {
      id: "3",
      title: "Regulatory: FDA Update June 2024",
      description: "Affects this claim",
      severity: "high",
      percentage: 88,
      isExpanded: false,
      sourceUrl: "fda.gov/updates",
    },
    {
      id: "4",
      title: "▲ Accessibility: Contrast",
      description: "Button text fails WCAG AA standard",
      severity: "medium",
      percentage: 93,
      isExpanded: false,
      sourceUrl: "acme.com/buttons",
    },
    {
      id: "5",
      title: "Copyright notice",
      description: "Missing copyright information in footer",
      severity: "low",
      percentage: 76,
      isExpanded: false,
      sourceUrl: "acme.com/footer",
    },
  ]);
  const [expandedRisks, setExpandedRisks] = useState<string[]>([]);

  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const [inputMessage, setInputMessage] = useState("");
  const [activeFilter, setActiveFilter] = useState<
    "all" | "high" | "medium" | "low"
  >("all");
  const [showDialog, setShowDialog] = useState(false);
  const [selectedRiskId, setSelectedRiskId] = useState<string | null>(null);

  const [isThinking, setIsThinking] = useState(false);
  const [chatSuggestions, setChatSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const [shouldAutoSend, setShouldAutoSend] = useState(false);

  // Auto-scroll to bottom when messages change, but only if user is at bottom
  const scrollToBottom = () => {
    if (!userHasScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Auto-send message when inputMessage is set and shouldAutoSend is true
  useEffect(() => {
    if (shouldAutoSend && inputMessage.trim()) {
      setShouldAutoSend(false);
      handleSendMessage(selectedRiskId);
    }
  }, [inputMessage, shouldAutoSend, selectedRiskId]);

  // Check if user has scrolled up
  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        messagesContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
      setUserHasScrolledUp(!isAtBottom);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isThinking]);

  const filteredRisks = (analysisResults?.results ?? [])?.filter((risk) => {
    if (activeFilter === "all") return true;
    return risk.priority.toLowerCase() === activeFilter;
  });

  const riskCounts = {
    all: (analysisResults?.results ?? []).length,
    high: (analysisResults?.results ?? []).filter(
      (r) => r.priority.toLowerCase() === "high",
    ).length,
    medium: (analysisResults?.results ?? []).filter(
      (r) => r.priority.toLowerCase() === "medium",
    ).length,
    low: (analysisResults?.results ?? []).filter(
      (r) => r.priority.toLowerCase() === "low",
    ).length,
  };

  const tabs = [
    { value: "all", label: "All", color: "gray" },
    { value: "high", label: "High", color: "red" },
    { value: "medium", label: "Medium", color: "amber" },
    { value: "low", label: "Low", color: "grass" },
  ];

  const getSeverityIcon = (priority: string, isLabel?: boolean) => {
    if (isLabel) {
      // Icons for priority labels (white icons on colored backgrounds)
      switch (priority.toLowerCase()) {
        case "high":
          return <Prohibit size={12} />;
        case "medium":
          return <Warning size={12} />;
        case "low":
          return <CheckCircle size={12} />;
        default:
          return null;
      }
    } else {
      // Original icons for the old layout (not used anymore but keeping for compatibility)
      switch (priority.toLowerCase()) {
        case "high":
          return (
            <div className="bg-red-500 flex size-6 items-center justify-center rounded-full">
              <Prohibit size={16} color="white" />
            </div>
          );
        case "medium":
          return (
            <div className="bg-yellow-500 flex size-6 items-center justify-center rounded-full">
              <Warning size={16} color="white" />
            </div>
          );
        case "low":
          return (
            <div className="bg-green-500 flex size-6 items-center justify-center rounded-full">
              <CheckCircle size={16} color="white" />
            </div>
          );
        default:
          return null;
      }
    }
  };

  const getSeverityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "high":
        return "#E89F8B"; // Muted coral/terracotta for high priority
      case "medium":
        return "#DDAA66"; // Muted goldenrod/sandy yellow for medium priority
      case "low":
        return "#8FC090"; // Muted sage green for low priority
      default:
        return "#6B7280";
    }
  };

  // Bright colors for priority chips and selected states (matching tab colors)
  const getPriorityChipColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "high":
        return "#EF4444"; // Bright red
      case "medium":
        return "#F59E0B"; // Bright amber
      case "low":
        return "#22C55E"; // Bright grass green
      default:
        return "#6B7280";
    }
  };

  const toggleRiskExpansion = (id: string) => {
    setRisks((prev) =>
      prev.map((risk) =>
        risk.id === id ? { ...risk, isExpanded: !risk.isExpanded } : risk,
      ),
    );
  };

  const handleSendMessage = async (
    risk_id?: string,
    overrideMessage?: string,
  ) => {
    if (!inputMessage.trim() && !overrideMessage?.trim()) return;

    setIsThinking(true);
    setMessages((prev) => {
      if (!overrideMessage) {
        return [
          ...prev,
          {
            id: Date.now().toString() + "user",
            type: "user",
            content: inputMessage,
            timestamp: Date.now(),
          },
          {
            id: Date.now().toString() + "assistant",
            type: "assistant",
            content: getRandomThinkingMessage(),
            timestamp: Date.now(),
          },
        ];
      } else {
        return [
          ...prev,
          {
            id: Date.now().toString(),
            type: "assistant",
            content: getRandomThinkingMessage(),
            timestamp: Date.now(),
          },
        ];
      }
    });
    setInputMessage("");
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: "user",
      content: overrideMessage || inputMessage,
      timestamp: Date.now(),
    };
    const response = await chatWithAgent({
      session_id: sessionId || "",
      message: overrideMessage || inputMessage,
      risk_id: risk_id,
    });

    setMessages((prev) => {
      if (overrideMessage) {
        return [
          ...prev,
          {
            ...response,
            id: Date.now().toString(),
            content: response.message,
            type: "assistant",
            subType: response.type,
          },
        ].filter((m) => !THINKING_MESSAGES.includes(m.content));
      }

      return [
        ...prev,
        {
          ...response,
          id: Date.now().toString(),
          content: response.message,
          type: "assistant",
          subType: response.type,
        },
      ].filter((m) => !THINKING_MESSAGES.includes(m.content));
    });
  };

  useEffect(() => {
    if (analysisResults?.results?.length > 0 && analysisResults?.chat_summary) {
      const suggestions = analysisResults?.chat_suggestions || [];
      setChatSuggestions(suggestions);
    }
  }, [analysisResults]);

  return (
    <div className="flex h-screen gap-4 bg-puntt-neutral-gray-2 p-4">
      {/* Left Panel - Risk Review */}
      <div className="flex w-1/2 flex-col rounded-lg bg-base-white shadow-sm">
        {/* Header */}
        <div className="border-gray-200 bg-white border-b px-6 pt-6">
          <h1 className="text-gray-900 mb-4 text-2xl font-bold">
            Risk Review for{" "}
            {analysisResults?.domain
              .replace(".com", "")
              .charAt(0)
              .toUpperCase() +
              analysisResults?.domain
                .replace(".com", "")
                .slice(1)
                .toLowerCase()}
          </h1>

          {/* Filter Tabs */}
          <Tabs.Root
            value={activeFilter}
            onValueChange={(value) =>
              setActiveFilter(value as "all" | "high" | "medium" | "low")
            }
          >
            <Tabs.List className="bg-white relative flex w-full items-center justify-between shadow-none">
              {/* Continuous grey line under all tabs */}
              <div className="bg-gray-200 absolute bottom-0 left-0 right-0 h-0.5"></div>

              {tabs.map((t) => (
                <Tabs.Trigger
                  key={t.value}
                  value={t.value}
                  className="bg-white relative flex-1"
                >
                  {t.label}{" "}
                  <Badge
                    // @ts-expect-error - TODO: fix this
                    color={t.color}
                    variant="solid"
                    size="1"
                    className="ml-2 flex h-6 w-6 items-center justify-center rounded-md text-xs font-medium"
                  >
                    {riskCounts[t.value as keyof typeof riskCounts]}
                  </Badge>
                  {/* Blue line that appears when tab is selected */}
                  {activeFilter === t.value && (
                    <div className="bg-blue-600 absolute bottom-0 left-0 right-0 h-0.5"></div>
                  )}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs.Root>
        </div>

        {/* Risk List - Scrollable */}
        <div
          className="bg-white flex-1 overflow-y-auto p-6 pb-32"
          onClick={(e) => {
            // Only deselect if clicking on the background, not on a risk card
            if (e.target === e.currentTarget) {
              setSelectedRiskId(null);
            }
          }}
        >
          <div className="space-y-4">
            {isLoadingResults ? (
              <RiskCardSkeleton count={3} />
            ) : filteredRisks.length > 0 ? (
              filteredRisks.map((risk, index) => (
                <>
                  <button
                    key={risk.id}
                    onClick={() =>
                      setSelectedRiskId(
                        selectedRiskId === risk.id ? null : risk.id,
                      )
                    }
                    className={cx(
                      "relative w-full cursor-pointer rounded-lg bg-base-white p-4 text-start transition-all duration-200 hover:shadow-md",
                      {
                        "scale-[1.02] border-2 shadow-lg":
                          selectedRiskId === risk.id,
                        "border hover:scale-[1.01] hover:border-2 hover:shadow-md":
                          selectedRiskId !== risk.id,
                      },
                    )}
                    style={{
                      borderColor:
                        selectedRiskId === risk.id
                          ? getPriorityChipColor(risk.priority.toLowerCase())
                          : getSeverityColor(risk.priority.toLowerCase()),
                    }}
                    data-testid={`risk-item-${risk.id}`}
                  >
                    {/* Priority Label - Top Left */}
                    <div
                      className="absolute left-3 top-3 flex items-center space-x-2 rounded-md px-3 py-1 text-xs font-medium"
                      style={{
                        backgroundColor: getPriorityChipColor(
                          risk.priority.toLowerCase(),
                        ),
                        color: "#FFFFFF",
                      }}
                    >
                      {getSeverityIcon(risk.priority, true)}
                      <span>
                        {risk.priority.toLowerCase() === "high"
                          ? "High Priority"
                          : risk.priority.toLowerCase() === "medium"
                            ? "Medium Priority"
                            : "Low Priority"}
                      </span>
                    </div>
                    <Tooltip content="Copies a secure link to the risk so others can review it.">
                      <Button
                        size="1"
                        className="absolute right-3 top-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          notify({
                            title: "Review Link Copied",
                            message: "Paste it into Slack, email, or Jira",
                          });
                        }}
                      >
                        <ShareNetworkIcon size={16} /> Share for Review
                      </Button>
                    </Tooltip>
                    {/* Risk Content */}
                    <Grid className="pt-8" gap="2">
                      <Text size="4" weight="medium">
                        {risk.title}
                      </Text>
                      <Text size="2" weight="regular">
                        <b>Why this matters:</b> {risk.impact_assessment}
                      </Text>

                      <Text size="2" weight="bold">
                        Evidence:
                      </Text>
                      <div className="rounded-lg border border-puntt-neutral-gray-8 bg-puntt-neutral-gray-2 p-2">
                        <Code size="1" weight="regular" variant="ghost">
                          {risk.evidence}
                        </Code>
                      </div>
                      <Grid className="mt-4 rounded-lg border bg-base-white p-1">
                        <Flex justify="between" align="center">
                          <Text size="1" weight="regular">
                            <a
                              href={risk.source_url}
                              target="_blank"
                              className="text-base-black hover:text-puntt-accent-11 active:text-puntt-accent-10"
                            >
                              {risk.source_url}
                            </a>
                          </Text>
                          <Button
                            size="1"
                            variant="outline"
                            className="w-fit"
                            onClick={() => {
                              if (expandedRisks.includes(risk.id)) {
                                setExpandedRisks((localRisks) =>
                                  localRisks.filter((id) => id !== risk.id),
                                );
                              } else {
                                setExpandedRisks((localRisks) => [
                                  ...localRisks,
                                  risk.id,
                                ]);
                              }
                            }}
                          >
                            View Full Source <ArrowsOutSimpleIcon />
                          </Button>
                        </Flex>
                        {expandedRisks.includes(risk.id) && (
                          <div className="relative size-full">
                            <img
                              src={risk.thumbnail_url || risk.image_url}
                              alt={risk.title}
                              className="size-full object-cover"
                            />
                            {/* Pin placement - positioned based on x,y percentages */}
                            <Pin
                              priority={risk.priority.toLowerCase()}
                              x={risk.x ?? 33}
                              y={risk.y ?? 33}
                            />
                          </div>
                        )}
                      </Grid>

                      <Separator className="w-full" />
                      <Flex align="center" gap="3">
                        <Text size="3" weight="medium">
                          Suggested Fix
                        </Text>
                        <Badge
                          size="1"
                          variant="outline"
                          color={
                            risk.fix_effort === "High"
                              ? "red"
                              : risk.fix_effort === "Medium"
                                ? "amber"
                                : "grass"
                          }
                        >
                          {risk.fix_effort} Effort
                        </Badge>
                      </Flex>
                      <Text size="2" weight="regular">
                        {risk.suggested_fix}
                      </Text>
                      <Text
                        size="1"
                        weight="regular"
                        className="text-puntt-neutral-gray-11"
                      >
                        Owner: {risk.owner} Team
                      </Text>
                    </Grid>
                  </button>
                </>
              ))
            ) : null}
          </div>
        </div>
      </div>

      {/* Right Panel - AI Chat */}
      <div className="flex w-1/2 flex-col overflow-hidden rounded-lg bg-base-white shadow-sm">
        {/* Messages Container - Scrollable */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-6"
          onScroll={handleScroll}
        >
          <div className="space-y-4">
            {!messages.length ? (
              <div className="flex gap-2.5">
                <RobotIcon />
                <Grid
                  className="flex-1 items-start space-x-3 rounded-xl"
                  gap="2"
                >
                  <QuickSuggestions
                    suggestions={analysisResults?.chat_suggestions || []}
                    onSuggestionClick={(suggestion) => {
                      if (!isThinking) {
                        setInputMessage(suggestion);
                        setShouldAutoSend(true);
                      }
                    }}
                    visible
                    isLoading={isLoadingResults}
                    enabled
                  />
                </Grid>
              </div>
            ) : (
              messages.map((message) => (
                <>
                  <ChatMessage
                    key={message.id}
                    message={message}
                    setIsThinking={setIsThinking}
                  />

                  {message.subType === "idk" && (
                    <ConnectMoreData onConnect={() => setShowDialog(true)} />
                  )}
                </>
              ))
            )}
            {/* Invisible element to scroll to */}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Chat Input Section */}
        <div className="px-6 pb-6">
          {/* Quick Suggestions or Risk Context */}
          {selectedRiskId && (
            <RiskContextIndicator
              selectedRiskTitle={
                filteredRisks.find((r) => r.id === selectedRiskId)?.title
              }
              visible={!!selectedRiskId}
              enabled={true}
            />
          )}

          {/* Text Input Area */}
          <div className="relative z-20 w-full rounded-lg bg-base-white">
            <TextArea
              size="3"
              className="w-full resize-none overflow-y-auto pb-3 pr-12"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onClick={() => {
                if (inputMessage === "Show me my biggest risk.") {
                  setInputMessage("");
                }
              }}
              onKeyPress={(e) =>
                e.key === "Enter" &&
                !isThinking &&
                handleSendMessage(selectedRiskId, undefined)
              }
              placeholder="Ask me about your compliance risks, brand rules, or recent regulation changes."
              style={{
                height: "120px",
                width: "100%",
                minWidth: "100%",
                maxWidth: "100%",
              }}
            />
            <Popover.Root>
              <Popover.Trigger>
                <button className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white absolute bottom-2 left-2 rounded-lg p-2 transition-colors disabled:cursor-not-allowed">
                  <PlusIcon size={16} />
                </button>
              </Popover.Trigger>
              <Popover.Content className="flex flex-col gap-2">
                <input
                  type="file"
                  className="hidden"
                  ref={fileInputRef}
                  accept=".pdf,.doc, .docx, .xls, .xlsx, .ppt, .pptx, .odt, .ods, .odp, .odg, .odf, .txt, .rtf"
                  onChange={() => {}}
                  multiple
                  data-testid="knowledge-settings-files-input"
                />
                <Button
                  size="1"
                  variant="ghost"
                  className="text-left"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Add Photos/Files
                </Button>
                <Button
                  size="1"
                  variant="ghost"
                  className="text-left"
                  onClick={() => setShowDialog(true)}
                >
                  Connect Tools
                </Button>
              </Popover.Content>
            </Popover.Root>

            <Button
              onClick={() => handleSendMessage(selectedRiskId, undefined)}
              disabled={!inputMessage.trim() || isThinking}
              className="absolute bottom-2 right-2 p-2"
            >
              <ArrowUp size={16} />
            </Button>
          </div>
        </div>
      </div>

      {/* Connected Apps Dialog */}
      <ConnectedAppsDialog
        showDialog={showDialog}
        onClose={() => setShowDialog(false)}
      />
    </div>
  );
}
